// @ts-nocheck
import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import CodeIcon from "@mui/icons-material/Code";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import useStyles from "../DaikinConsistency.style";
import { openEntityJson, toEntityJson } from "./shared";
import ConsistencyLineView, { ConsistencyLineSummaryView } from "./ConsistencyLineView";

const IssuesDialog = ({ line, open, onClose }) => {
  const classes = useStyles();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography fontWeight={700}>All Issues</Typography>
          {line.summary.highPriority > 0 && (
            <Chip label={`${line.summary.highPriority} high`} color="error" size="small" className={classes.chipMd} />
          )}
          {line.summary.totalIssues - line.summary.highPriority > 0 && (
            <Chip
              label={`${line.summary.totalIssues - line.summary.highPriority} med`}
              color="warning"
              size="small"
              className={classes.chipMd}
            />
          )}
          <IconButton size="small" onClick={onClose} sx={{ ml: "auto" }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2.5, height: "min(900px, 75vh)", overflow: "auto" }}>
        <ConsistencyLineSummaryView line={line} />
      </DialogContent>
    </Dialog>
  );
};

const ConsistencyView = ({ result, selectedLine, onLineChange }) => {
  const classes = useStyles();
  const [grouping, setGrouping] = useState("summary");
  const [issuesOpen, setIssuesOpen] = useState(false);
  const [jsonOpen, setJsonOpen] = useState(false);
  const lineIds = Object.keys(result.lines);
  const currentLineId = lineIds[selectedLine];
  const currentLine = currentLineId ? result.lines[currentLineId] : null;

  if (lineIds.length === 0) {
    return <Alert severity="info">No Daikin lines found on this device.</Alert>;
  }

  return (
    <>
      <Tabs value={selectedLine} onChange={(_, v) => onLineChange(v)} className={classes.tabsBar}>
        {lineIds.map((lineId) => {
          const line = result.lines[lineId];
          return (
            <Tab
              key={lineId}
              label={
                <Box className={classes.tabLabel}>
                  <span>
                    Line {lineId} ({line.brand})
                  </span>
                  {line.summary.highPriority > 0 && (
                    <Chip label={line.summary.highPriority} color="error" size="small" className={classes.chipMd} />
                  )}
                </Box>
              }
            />
          );
        })}
      </Tabs>
      {currentLine && (
        <Box>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography variant="caption" color="text.secondary">
                {currentLine.summary.totalIssues} issue(s) — {currentLine.summary.highPriority} high priority
              </Typography>
              {grouping !== "summary" && (
                <IconButton
                  size="small"
                  onClick={() => setIssuesOpen(true)}
                  title="Show all issues"
                  sx={{ p: 0.25, opacity: 0.5, "&:hover": { opacity: 1 } }}
                >
                  <FormatListBulletedIcon sx={{ fontSize: 13 }} />
                </IconButton>
              )}
              <IconButton
                size="small"
                onClick={() => setJsonOpen(true)}
                title="Show JSON"
                sx={{ p: 0.25, opacity: 0.5, "&:hover": { opacity: 1 } }}
              >
                <CodeIcon sx={{ fontSize: 13 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => openEntityJson(currentLine)}
                title="Open as JSON in new tab"
                sx={{ p: 0.25, opacity: 0.5, "&:hover": { opacity: 1 } }}
              >
                <OpenInNewIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Box>
            <Box display="flex" gap={0.5}>
              <Button
                size="small"
                variant={grouping === "system" ? "contained" : "outlined"}
                onClick={() => setGrouping("system")}
              >
                By System
              </Button>
              <Button
                size="small"
                variant={grouping === "type" ? "contained" : "outlined"}
                onClick={() => setGrouping("type")}
              >
                By Type
              </Button>
              <Button
                size="small"
                variant={grouping === "summary" ? "contained" : "outlined"}
                onClick={() => setGrouping("summary")}
              >
                Summary
              </Button>
            </Box>
          </Box>
          <ConsistencyLineView line={currentLine} grouping={grouping} />
        </Box>
      )}
      {currentLine && <IssuesDialog line={currentLine} open={issuesOpen} onClose={() => setIssuesOpen(false)} />}
      {currentLine && (
        <Dialog open={jsonOpen} onClose={() => setJsonOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ pb: 1 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography fontWeight={700}>JSON</Typography>
              <IconButton
                size="small"
                onClick={() => openEntityJson(currentLine)}
                title="Open in new tab"
                sx={{ ml: 0.5 }}
              >
                <OpenInNewIcon sx={{ fontSize: 15 }} />
              </IconButton>
              <IconButton size="small" onClick={() => setJsonOpen(false)} sx={{ ml: "auto" }}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            <Box component="pre" sx={{ fontSize: "0.72rem", fontFamily: "monospace", overflow: "auto", m: 0, p: 2 }}>
              {toEntityJson(currentLine)}
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default ConsistencyView;
